/**
 * Browser-side JSON helpers for the /api envelope: {ok:true,data} | {ok:false,error}.
 * SSR loaders use apiFetch() from ~/lib/api-client instead — never these.
 */

import type { ApiResult } from './types';

async function request<T>(path: string, init: RequestInit): Promise<ApiResult<T>> {
  try {
    const res = await fetch(path, { credentials: 'include', ...init });
    const body = (await res.json().catch(() => null)) as ApiResult<T> | null;
    if (body && typeof body === 'object' && 'ok' in body) return body;
    if (res.ok) return { ok: true, data: (body ?? {}) as T };
    return { ok: false, error: `request_failed_${res.status}` };
  } catch {
    return { ok: false, error: 'network_error' };
  }
}

export function postJson<T>(path: string, body: unknown): Promise<ApiResult<T>> {
  return request<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function putJson<T>(path: string, body: unknown): Promise<ApiResult<T>> {
  return request<T>(path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function deleteJson<T>(path: string): Promise<ApiResult<T>> {
  return request<T>(path, { method: 'DELETE' });
}

export function postForm<T>(path: string, form: FormData): Promise<ApiResult<T>> {
  return request<T>(path, { method: 'POST', body: form });
}

/** Human-readable message for API error codes we know about. */
export function errorMessage(code: string): string {
  const known: Record<string, string> = {
    not_found: 'Not found.',
    invalid_input: 'Something in the form needs a second look.',
    rate_limited: 'Too many requests — give it a minute and try again.',
    turnstile_failed: 'The security check didn’t pass. Refresh and try again.',
    network_error: 'Connection hiccup — check your network and try again.',
  };
  return known[code] ?? 'Something went wrong. Please try again.';
}
